<script>
	import Image from '$lib/Components/Layout/Image/Image.svelte';
	import Section from '$lib/Components/Layout/Section/Section.svelte';
	import GetAFreeEstimate from '$lib/PageSections/GetAFreeEstimate/GetAFreeEstimate.svelte';

	import ashleyInstallingIndowInsertImg from './images/ashley-installing-indow-insert.jpg';
	import commonwealthHotelImg from './images/commonwealth-hotel.jpg';
	import ladyInstallingSolutionLightUvIndowInsertsImg from './images/lady-installing-solution-light-uv-indow-inserts.jpg';
	import perfectStormWindowsImg from './images/perfect-storm-windows.webp';
	import solutionsLightUvImg from './images/solutions-light-uv.png';
</script>

<Section>
	<Image
		src={commonwealthHotelImg}
		alt="interior view of soundproof apartment with large windows facing the street"
	/>
	<p>
		Are you finding yourself getting distracted on your Zoom calls or are your
		afternoon naps disrupted due to noisy traffic and neighbors? Have you
		recently searched, “How to soundproof an apartment?” You've come to the
		right place.
	</p>
	<p>
		Let's look at how to soundproof your apartment with ways that range from
		simple room additions to small home projects.
	</p>
	<p>
		To get a <a href="/resources/blog/most-effective-soundproofing-materials"
			>soundproof apartment</a
		>, just a few simple tips and projects can dramatically reduce noise in your
		apartment and create the stress-free refuge you want to achieve.
	</p>
</Section>

<Section>
	<Image
		src={perfectStormWindowsImg}
		alt="homeowner installing indow perfect seal storm windows"
	/>
	<h2>1. Determine Your Weak Spots</h2>
	<p>
		Any unwanted noise you hear is considered to be “leaking” sound because
		sound waves behave like a fluid. Unwanted noise moves to the weakest points
		of your apartment or condo and slips into your home through holes, gaps,
		crevices, and cracks. The first thing you need before you start
		soundproofing is to <a href="/resources/case-studies/street-noise-reduction"
			>locate your weak spots</a
		>, whether that's your walls, doors, or windows.
	</p>
	<p>
		After you determine your spots, you can plan your soundproofing accordingly.
	</p>
</Section>

<Section>
	<h2>2. Add Bookshelves</h2>
	<p>
		Using heavy books on multiple bookshelves is a fairly effective way to
		create a sound barrier. When combined, shelves and books create a thick
		barrier that sound has a hard time penetrating, essentially providing a
		second wall to your apartment. If your sound leaks are coming from a wall
		shared by a neighbor, this is a great choice.
	</p>
	<p>
		If you choose this option, get extra tall bookcases (think 5 shelves or
		more) and fill them as much as possible. There are some eclectic bookcases
		on the market, but a traditional wooden bookcase works best for ultimate
		sound stoppage.
	</p>
	<Image
		src={ashleyInstallingIndowInsertImg}
		alt="woman installing soundproof window inserts to reduce noise in apartment"
	/>
</Section>

<Section>
	<h2>3. Add Carpet, Rugs, and Pads</h2>
	<p>
		Plush carpet, rugs, and sound dampening pads can help absorb sound, along
		with wall hangings, upholstered furniture, and even plants. These materials
		also lessen the sound of footsteps and objects dropping within the
		apartment, minimizing the echo and absorbing the noise. You'll not only make
		your space quieter, but your neighbors will thank you, too.
	</p>
	<p>
		The thicker the carpet, rugs, or wall hangings, the better sound absorption
		they can provide. Today, you can even purchase wall hangings and rugs
		specifically made for soundproofing. Both options are usually denser than
		typical rugs and tapestries, often made with rubber and felt backings.
	</p>
</Section>

<Section>
	<h2>4. Invest in Soundproofing Panels or Curtains</h2>
	<p>
		You can create a soundproof room by installing wall panels or installing
		<a href="/resources/blog/diy-window-treatments">soundproof curtains</a>.
		They will help keep out sound, but you will need to hammer or drill holes
		into the walls to hang the panels.
	</p>
	<p>
		While an affordable and easy option, wall panels and curtains need to be
		installed carefully which may also require additional fabric depending on
		your home's aesthetic or personal taste. While your landlord probably won't
		have a problem with you installing curtains, they'll only work when closed
		and no one wants to live in a cave.
	</p>
	<Image
		src={solutionsLightUvImg}
		alt="apartment noise reduction that allows light"
	/>
</Section>

<Section>
	<h2>5. White Noise Machines</h2>
	<p>
		While not technically soundproofing, <a
			href="https://resonics.co.uk/soundproofing-your-apartment-myths-advice"
			target="_blank"
			rel="noreferrer">white noise</a
		> is broad-band noise signals played as a constant. To put it simply, white noise
		machines are able to drown out other noises in your immediate environment that
		would otherwise interfere with your apartment's noise level.
	</p>
	<p>
		By playing a non-distracting form of noise, white noise machines divert your
		attention by masking any disruptive noise sources. However, not everyone
		enjoys the sound of white noise, and your apartment could feel turbulent if
		you layer your TV or music over the white noise.
	</p>
</Section>

<Section>
	<h2>6. Seal Your Doors</h2>
	<p>
		One of the biggest culprits of noise pollution enters through your front
		door. Noise from busy corridors finds its way into your apartment through
		tiny gaps around the door's edge. Noise can also resonate through the door's
		core as well if you don't have a sturdy, heavy door, which is likely, as
		most apartment doors are hollow to save on costs.
	</p>
	<p>
		If you are able to, replace your hollow door with a solid, heavy-set door.
		If that's not affordable or allowed your best bet is to purchase an acoustic
		door seal kit and attach it to your current door. These work to eliminate
		the gaps around the door's edge and prevent noise transfer.
	</p>
	<p>
		However, if you don't want to purchase a sealing kit, a budget-friendly
		option is to use pipe insulation foam to apply to the base of the door. If
		you combine this with some weatherproofing strips, you can get a decent
		noise-canceling seal.
	</p>
	<Image
		src={ladyInstallingSolutionLightUvIndowInsertsImg}
		alt="woman installing soundproofing window insert for apartment noise reduction"
	/>
</Section>

<Section>
	<h2>7. Install Soundproof Window Inserts</h2>
	<p>
		<a href="/resources/soundproofing-windows">Soundproofing window inserts</a> provide
		window insulation that both reduces and absorbs sound. Installing window inserts
		is an easy and effective way of soundproofing that won't break the bank and won't
		cause any damage, so you probably won't need your landlord's permission to install
		them.
	</p>
	<p>
		Indow Acoustic Grade window inserts can reduce and cancel noise — up to 70%
		of outside noise when placed over single-pane windows and up to 50% of noise
		coming through double-pane windows*. The inserts, edged with silicone
		compression tubing, just press into your existing window frame to form a
		seal with no hassle, nails, or adhesives required.
	</p>
	<p>
		They're <a href="/solutions/window-noise-reduction">easy to install</a> and they
		not only block outside noise but also prevent inside noise from bothering your
		neighbors.
	</p>
	<p>
		Getting an exact fit is essential for soundproofing because any gaps or
		spaces allow sound to seep inside. Indow inserts offer a precise fit to
		create a more comfortable, energy-efficient space with a profile that allows
		it to <a href="/resources/faq/#design">fit into your window frame</a>. They
		are custom-made so they can offer simple soundproofing for any window shape.
	</p>
	<p>
		Want to get started on soundproofing your apartment or condo with Indow
		Window Inserts? Check out <a href="/">Indow window inserts</a> to see the possibilities.
	</p>
	<p class="italic">
		*We want to ensure our inserts help solve your noise problem. Overall noise
		reduction depends on the amount of noise coming through your walls,
		ceilings, floors, doors, and existing windows. Noise reduction will be less
		when placed over double-pane windows. Read our full <a
			href="/noise-disclaimer">Noise Reduction Sheet</a
		> to learn more.
	</p>
</Section>

<GetAFreeEstimate backgroundColor="blue" title="Let Us Help You" />
